/**
 * EmployeeQuickSearch
 * Search field for finding employees by CPF or employeeCode (matrícula)
 * Used in appointment form to quickly find employees across companies.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person,
  Business,
  Badge,
  PersonAdd,
} from '@mui/icons-material';
import employeeService from '../../../services/employeeService';
import { Employee } from '../../../types/employee';
import { formatCPF } from '../../../utils/employeeFormatters';

interface EmployeeQuickSearchProps {
  onEmployeeFound: (employee: Employee) => void;
  onNotFound: (query: string) => void;
  disabled?: boolean;
}

type SearchState = 'idle' | 'searching' | 'found' | 'not_found' | 'error';

export const EmployeeQuickSearch: React.FC<EmployeeQuickSearchProps> = ({
  onEmployeeFound,
  onNotFound,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [foundEmployee, setFoundEmployee] = useState<Employee | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setSearchState('searching');
    setFoundEmployee(null);
    setErrorMessage('');

    try {
      const employee = await employeeService.searchByIdentifier(trimmed);
      if (employee) {
        setSearchState('found');
        setFoundEmployee(employee);
      } else {
        setSearchState('not_found');
      }
    } catch {
      setSearchState('error');
      setErrorMessage('Erro ao buscar funcionário. Tente novamente.');
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = () => {
    if (foundEmployee) {
      onEmployeeFound(foundEmployee);
      setQuery('');
      setSearchState('idle');
      setFoundEmployee(null);
    }
  };

  const handleRegisterNew = () => {
    const cleaned = query.replace(/\D/g, '');
    onNotFound(cleaned.length === 11 ? cleaned : '');
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Apply CPF mask if input looks like CPF (only digits, up to 14 chars with mask)
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 11 && /^\d*$/.test(digits)) {
      // Auto-format as CPF while typing
      if (digits.length > 9) {
        setQuery(`${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`);
      } else if (digits.length > 6) {
        setQuery(`${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`);
      } else if (digits.length > 3) {
        setQuery(`${digits.slice(0, 3)}.${digits.slice(3)}`);
      } else {
        setQuery(digits);
      }
    } else {
      // Allow alphanumeric for employeeCode
      setQuery(value);
    }
    // Reset search state when user changes input
    if (searchState !== 'idle' && searchState !== 'searching') {
      setSearchState('idle');
      setFoundEmployee(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          label="Buscar por CPF ou Matrícula"
          placeholder="000.000.000-00 ou EMP001001"
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || searchState === 'searching'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchState === 'searching' ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={disabled || !query.trim() || searchState === 'searching'}
          sx={{ minWidth: 100, height: 40 }}
        >
          Buscar
        </Button>
      </Box>

      {/* Found employee card */}
      {searchState === 'found' && foundEmployee && (
        <Card variant="outlined" sx={{ mt: 1.5, bgcolor: 'success.50', borderColor: 'success.main' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Person fontSize="small" color="success" />
                  <Typography variant="subtitle2">{foundEmployee.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Badge fontSize="inherit" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      CPF: {formatCPF(foundEmployee.cpf)}
                    </Typography>
                  </Box>
                  {foundEmployee.company && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Business fontSize="inherit" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {foundEmployee.company.tradeName || foundEmployee.company.legalName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleSelect}
              >
                Selecionar
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Not found alert */}
      {searchState === 'not_found' && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={
            <Button
              color="warning"
              size="small"
              startIcon={<PersonAdd />}
              onClick={handleRegisterNew}
            >
              Cadastrar Novo
            </Button>
          }
        >
          Funcionário não encontrado para a busca informada.
        </Alert>
      )}

      {/* Error alert */}
      {searchState === 'error' && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
};
